'use client';

import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { useState } from 'react';
import Link from 'next/link';
import { cn } from '@/shared/lib/cn';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';
import { Button } from '@/shared/ui/shadcn/button';
import { formatPackPrice } from '@/shared/lib/format-catalog-price';
import { useProductCheckout } from '@/features/payment/hooks/use-product-checkout';
import { CheckoutRailChooserDialog } from '@/features/payment/ui/CheckoutRailChooserDialog';
import type { TokenPackage } from '@/types/tokens';

/** Flat tokens/render fallback used when the server doesn't ship `avg_renders`. */
const TOKENS_PER_RENDER_ESTIMATE = 3;

const plansLinkChunks = (chunks: ReactNode) => (
  <Link
    href="/account/tokens"
    className="text-primary"
    onClick={(e) => e.stopPropagation()}
  >
    {chunks}
  </Link>
);

/**
 * A single token package card — the thin price pill launches a direct Paddle
 * checkout for that package. Shared between the account/tokens storefront
 * list and the token-paywall dialog so the two surfaces never drift.
 */
export function TokenPackageCard({
  package: pkg,
  hideUnavailabilityMessage,
  onCheckoutOpen,
  onCheckoutClose,
  onPurchased,
  variant = 'sheet',
}: {
  package: TokenPackage;
  hideUnavailabilityMessage?: boolean;
  /**
   * Surface treatment. `sheet` (default) — transparent fill + hairline border,
   * for the token-paywall dialog's grouped-gray sheet. `storefront` — white
   * card, NO outline, shadow only: the account/tokens storefront rule is that
   * every product card is a plain white surface (Apple parity).
   */
  variant?: 'sheet' | 'storefront';
  /**
   * Paddle overlay launched — the host relaxes its modal lock so the overlay
   * (mounted at `<body>` level, OUTSIDE the host Drawer) stays clickable. Only
   * passed when this card lives inside a modal sheet (the INSUFFICIENT_TOKENS
   * paywall); the full-page storefront leaves it undefined (no lock to relax).
   */
  onCheckoutOpen?: () => void;
  /** Overlay dismissed (purchase OR cancel) — the host re-locks. */
  onCheckoutClose?: () => void;
  /**
   * Payment confirmed (`checkout.completed`) — the host flips to the in-sheet
   * success celebration for THIS package (parity with Apple/Android, which
   * both celebrate in place instead of vanishing). Passed by the paywall sheet;
   * the storefront leaves it undefined (its balance hero is the confirmation,
   * same as Apple's Tokens screen).
   */
  onPurchased?: (purchased: TokenPackage) => void;
}) {
  const t = useTranslations('tokenPackages');
  const {
    tokens,
    availability,
    avg_renders,
    savings_percent,
  } = pkg.attributes;

  // Direct Paddle checkout from the price pill (no intermediate page). The
  // descriptor is server-provided; `available` is false when the country
  // isn't served on a web rail or Paddle isn't configured, and the pill then
  // degrades to a muted informational badge (same pattern as PassCard).
  const { available, isLaunching, startCheckout } = useProductCheckout({
    descriptor: pkg.attributes.checkout,
    kind: 'token',
    sku:
      pkg.attributes.provider_apple_product_id ??
      pkg.attributes.provider_google_product_id ??
      `ai.getfacade.tokens.${tokens}`,
    callbacks: {
      onPurchased: onPurchased ? () => onPurchased(pkg) : undefined,
      onClosed: onCheckoutClose,
    },
  });
  // Payment-rail chooser (Paddle vs T-Bank) — opened by the price pill when
  // the server shipped a `checkout_alternative` (RU-locale buyers on Paddle).
  const alternative = pkg.attributes.checkout_alternative;
  const [chooserOpen, setChooserOpen] = useState(false);

  const isUnavailable = !available || availability?.is_available === false;
  const formattedPrice = formatPackPrice(pkg.attributes);
  const displayRenders =
    avg_renders > 0
      ? avg_renders
      : Math.floor(tokens / TOKENS_PER_RENDER_ESTIMATE);

  const getUnavailabilityMessage = (): ReactNode => {
    if (!availability || availability.is_available) return null;
    switch (availability.reason) {
      case 'daily_limit':
        return t('unavailableDaily');
      case 'high_balance':
        return t('unavailableHighBalance');
      case 'account_blocked':
        return t('unavailableBlocked');
      case 'subscription_required':
        return t.rich('unavailableSubscription', { link: plansLinkChunks });
      default:
        return t('unavailable');
    }
  };

  return (
    <div
      className={cn(
        'relative rounded-xl p-4',
        // Sheet (token paywall): a FILLED option card — white on the #F7F8FB
        // sheet, #1C1C1E (a tier darker than the #2C2C2E sheet) in dark. Same
        // hexes as Apple `cardOnSheet` / Android `paywallCardSurface`. It used
        // to be a transparent fill + hairline, which read as a list of text
        // rather than a set of choices. The hairline stays at 12% (down from
        // 20%): the fill separates now, the border only crisps the edge.
        // Storefront: white card, no outline, shadow only — same surface as
        // every other product card on the account/tokens page.
        variant === 'storefront'
          ? 'bg-card shadow-md'
          : 'border border-foreground/[0.12] bg-white dark:bg-[#1c1c1e]'
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <span
              className={`text-token-balance whitespace-nowrap ${isUnavailable ? 'text-muted-foreground' : ''}`}
            >
              {tokens}
            </span>
            <WalnutIcon
              className={`h-5 w-4 text-[#C77B4A] ${isUnavailable ? 'opacity-50' : ''}`}
            />
            {savings_percent > 0 && (
              <span className="text-card-meta whitespace-nowrap rounded-full bg-primary/[0.08] px-2 py-0.5 font-semibold text-[#055C58]">
                {t('savePercent', { percent: savings_percent })}
              </span>
            )}
          </div>
          {displayRenders > 0 && (
            <span className="text-card-meta text-muted-foreground/70">
              {t('avgRenders', { count: displayRenders })}
            </span>
          )}
        </div>

        {isUnavailable ? (
          <span className="inline-flex h-auto min-h-8 min-w-[84px] max-w-[45%] shrink items-center justify-center whitespace-normal break-words rounded-full bg-muted px-5 py-1 text-center text-list-item-value text-muted-foreground">
            {formattedPrice}
          </span>
        ) : (
          <Button
            className="h-auto min-h-8 min-w-[84px] max-w-[45%] shrink whitespace-normal break-words rounded-full px-5 py-1 text-center text-list-item-value"
            disabled={isLaunching}
            onClick={() => {
              // Two rails available → let the buyer pick in a modal instead of
              // launching Paddle straight away.
              if (alternative) {
                setChooserOpen(true);
                return;
              }
              // Relax the host modal lock BEFORE launching so Paddle's
              // body-level overlay is clickable the moment it mounts (parity
              // with PassBuyCta.onStart / PackBuyCta.onLaunch).
              onCheckoutOpen?.();
              void startCheckout();
            }}
          >
            {formattedPrice}
          </Button>
        )}
      </div>
      {!hideUnavailabilityMessage &&
        isUnavailable &&
        getUnavailabilityMessage() && (
          <p className="text-form-help text-muted-foreground mt-2">
            {getUnavailabilityMessage()}
          </p>
        )}
      {/* Secondary rail (T-Bank, RF-issued cards) for RU-locale buyers whose
          primary pill opens Paddle. Server-gated: null unless both rails apply. */}
      {!isUnavailable && alternative && (
        <CheckoutRailChooserDialog
          open={chooserOpen}
          onOpenChange={setChooserOpen}
          alternative={alternative}
          kind="token"
          primaryPriceLabel={formattedPrice}
          onPickPrimary={() => {
            setChooserOpen(false);
            onCheckoutOpen?.();
            void startCheckout();
          }}
        />
      )}
    </div>
  );
}
